import React, { useEffect, useState, useCallback } from "react";
import { Edit, Save, X, RotateCw, Wifi, WifiOff } from "lucide-react";
import OperatorCardActions from "../components/OperatorCardActions";
import { fetchOperatorSockets, updateSocketItem } from "../services/sockets";

const formatLastSeen = (value) => {
    if (!value) return "(Ніколи)";
    try {
        return new Date(value).toLocaleString("uk-UA");
    } catch (_) {
        return "(Ніколи)";
    }
};

const maskBlePin = (value) => {
    if (!value) return "(Не задано)";
    return "•".repeat(String(value).length);
};

const SocketsOperators = () => {
    const [sockets, setSockets] = useState([]);
    const [filteredSockets, setFilteredSockets] = useState([]);
    const [message, setMessage] = useState("");
    const [searchQuery] = useState("");
    const [sortBy, setSortBy] = useState("name");
    const [sortDir, setSortDir] = useState("asc");
    const [editingSocketId, setEditingSocketId] = useState(null);
    const [editedData, setEditedData] = useState({
        name: "",
        descriptions: "",
        ble_access_code: "",
        status: false,
    });
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [expandedSocketId, setExpandedSocketId] = useState(null);

    const loadSockets = useCallback(async () => {
        try {
            const data = await fetchOperatorSockets();
            if (Array.isArray(data)) {
                setSockets(data);
            } else {
                console.error("Очікувався масив, а прийшло:", data);
                setSockets([]);
            }
        } catch (error) {
            setMessage(`❌ Помилка: ${error.message}`);
        }
    }, []);

    useEffect(() => {
        loadSockets();

        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [loadSockets]);

    useEffect(() => {
        const q = searchQuery.toLowerCase();
        const filtered = sockets.filter((socket) =>
            (socket.name || "").toString().toLowerCase().includes(q) ||
            (socket.descriptions || "").toString().toLowerCase().includes(q)
        );
        setFilteredSockets(filtered);
    }, [sockets, searchQuery]);

    const toggleSort = (field) => {
        if (sortBy === field) {
            setSortDir(sortDir === "asc" ? "desc" : "asc");
        } else {
            setSortBy(field);
            setSortDir("asc");
        }
    };

    const getSortedData = () => {
        if (!filteredSockets) return [];
        const sorted = [...filteredSockets];
        sorted.sort((a, b) => {
            let valA = a[sortBy] || "";
            let valB = b[sortBy] || "";
            valA = String(valA).toLowerCase();
            valB = String(valB).toLowerCase();
            if (sortDir === "asc") {
                return valA > valB ? 1 : valA < valB ? -1 : 0;
            } else {
                return valA < valB ? 1 : valA > valB ? -1 : 0;
            }
        });
        return sorted;
    };

    const handleEdit = (socket) => {
        setEditingSocketId(socket.id);
        setExpandedSocketId(socket.id);
        setEditedData({
            name: socket.name,
            descriptions: socket.descriptions || "",
            ble_access_code: socket.ble_access_code || "",
            status: socket.status || false,
        });
    };

    const toggleSocketExpanded = (socketId, isEditing) => {
        if (isEditing) return;
        setExpandedSocketId((prev) => (prev === socketId ? null : socketId));
    };

    const handleSave = async (id) => {
        try {
            if (!editedData.status) {
                const confirmDisable = window.confirm(
                    "Ви дійсно хочете вимкнути цю розетку? Всі пристрої, прив'язані до неї, будуть переведені в режим 'Обслуговування'."
                );
                if (!confirmDisable) {
                    setEditingSocketId(null);
                    return; // відміна дії
                }
            }

            await updateSocketItem(id, editedData);
            setEditingSocketId(null);
            loadSockets();
        } catch (error) {
            setMessage(`❌ Помилка: ${error.message}`);
        }
    };

    const renderMobileView = () => (
        <div className="operator-accordion-list">
            {getSortedData().map((socket) => {
                const isEditing = editingSocketId === socket.id;
                const isExpanded = expandedSocketId === socket.id || isEditing;
                const statusLabel = socket.status ? "Активний" : "Вимкнений";
                const onlineLabel = socket.is_online ? "Онлайн" : "Офлайн";

                return (
                    <div key={socket.id} className="operator-accordion-item config-card">
                        <button
                            type="button"
                            className="operator-accordion-head"
                            onClick={() => toggleSocketExpanded(socket.id, isEditing)}
                            aria-expanded={isExpanded}
                        >
                            <div className="operator-accordion-head-main">
                                <div className="operator-accordion-title">
                                    {socket.is_online ? (
                                        <Wifi size={14} style={{ color: "green", marginRight: 6, verticalAlign: "middle" }} />
                                    ) : (
                                        <WifiOff size={14} style={{ color: "red", marginRight: 6, verticalAlign: "middle" }} />
                                    )}
                                    {isEditing ? editedData.name : socket.name}
                                </div>
                                <div className="operator-accordion-meta">{statusLabel} • {onlineLabel}</div>
                            </div>
                            <div className="operator-accordion-caret">{isExpanded ? "▾" : "▸"}</div>
                        </button>

                        <OperatorCardActions
                            editing={isEditing}
                            onEdit={() => handleEdit(socket)}
                            onSave={() => handleSave(socket.id)}
                            onCancel={() => setEditingSocketId(null)}
                        />

                        {isExpanded ? (
                            <div className="operator-accordion-body">
                                <div><strong>Назва:</strong> {isEditing ? (
                                    <input
                                        value={editedData.name}
                                        onChange={(e) =>
                                            setEditedData({ ...editedData, name: e.target.value })
                                        }
                                    />
                                ) : socket.name}</div>

                                <div><strong>Опис:</strong> {isEditing ? (
                                    <input
                                        value={editedData.descriptions}
                                        onChange={(e) =>
                                            setEditedData({
                                                ...editedData,
                                                descriptions: e.target.value,
                                            })
                                        }
                                    />
                                ) : (
                                    socket.descriptions || "(Немає)"
                                )}</div>

                                <div><strong>IP адреса:</strong> {socket.ip_address || "(Немає)"}</div>
                                <div><strong>MAC адреса:</strong> {socket.mac_address || "(Немає)"}</div>
                                <div><strong>Публічна IP:</strong> {socket.public_ip || "(Невідома)"}</div>
                                <div><strong>BLE PIN:</strong> {isEditing ? (
                                    <input
                                        value={editedData.ble_access_code}
                                        onChange={(e) =>
                                            setEditedData({
                                                ...editedData,
                                                ble_access_code: e.target.value,
                                            })
                                        }
                                        placeholder="4-8 цифр або пусто"
                                        inputMode="numeric"
                                    />
                                ) : (
                                    maskBlePin(socket.ble_access_code)
                                )}</div>
                                <div><strong>Останній контакт:</strong> {socket.last_seen ? new Date(socket.last_seen).toLocaleString("uk-UA") : "(Ніколи)"}</div>

                                <div><strong>Статус:</strong> {isEditing ? (
                                    <select
                                        value={editedData.status ? 1 : 0}
                                        onChange={(e) =>
                                            setEditedData({
                                                ...editedData,
                                                status: e.target.value === "1",
                                            })
                                        }
                                    >
                                        <option value="1">Активний</option>
                                        <option value="0">Вимкнений</option>
                                    </select>
                                ) : statusLabel}</div>
                            </div>
                        ) : null}
                    </div>
                );
            })}
        </div>
    );

    const renderDesktopView = () => (
        <div className="table-responsive compact">
            <table>
                <thead>
                    <tr>
                        <th>Онлайн</th>
                        <th>IP адреса</th>
                        <th>Публічна IP</th>
                        <th>Firmware</th>
                        <th>Останній контакт</th>
                        <th style={{ cursor: "pointer" }} onClick={() => toggleSort("name")}>
                            Назва {sortBy === "name" && (sortDir === "asc" ? "▲" : "▼")}
                        </th>
                        <th style={{ cursor: "pointer" }} onClick={() => toggleSort("descriptions")}>
                            Опис {sortBy === "descriptions" && (sortDir === "asc" ? "▲" : "▼")}
                        </th>
                        <th>BLE PIN</th>
                        <th style={{ cursor: "pointer" }} onClick={() => toggleSort("status")}>
                            Статус {sortBy === "status" && (sortDir === "asc" ? "▲" : "▼")}
                        </th>
                        <th>Дії</th>
                    </tr>
                </thead>
                <tbody>
                    {getSortedData().map((socket) => (
                        <tr key={socket.id}>
                            <td>
                                {socket.is_online ? (
                                    <Wifi size={18} style={{ color: "green" }} />
                                ) : (
                                    <WifiOff size={18} style={{ color: "red" }} />
                                )}
                            </td>
                            <td>
                                {socket.ip_address || "(Немає)"}
                            </td>
                            <td>
                                {socket.public_ip || "(Немає)"}
                            </td>
                            <td>
                                {socket.firmware_version || "(Немає)"}
                            </td>
                            <td>
                                {formatLastSeen(socket.last_seen)}
                            </td>
                            <td>
                                {editingSocketId === socket.id ? (
                                    <input
                                        value={editedData.name}
                                        onChange={(e) =>
                                            setEditedData({ ...editedData, name: e.target.value })
                                        }
                                    />
                                ) : (
                                    socket.name
                                )}
                            </td>
                            <td>
                                {editingSocketId === socket.id ? (
                                    <input
                                        value={editedData.descriptions}
                                        onChange={(e) =>
                                            setEditedData({
                                                ...editedData,
                                                descriptions: e.target.value,
                                            })
                                        }
                                    />
                                ) : (
                                    socket.descriptions
                                )}
                            </td>
                            <td>
                                {editingSocketId === socket.id ? (
                                    <input
                                        value={editedData.ble_access_code}
                                        onChange={(e) =>
                                            setEditedData({
                                                ...editedData,
                                                ble_access_code: e.target.value,
                                            })
                                        }
                                        placeholder="4-8 цифр або пусто"
                                        inputMode="numeric"
                                    />
                                ) : (
                                    maskBlePin(socket.ble_access_code)
                                )}
                            </td>
                            <td className="text-center">
                                {editingSocketId === socket.id ? (
                                    <select
                                        value={editedData.status ? 1 : 0}
                                        onChange={(e) =>
                                            setEditedData({
                                                ...editedData,
                                                status: e.target.value === "1",
                                            })
                                        }
                                    >
                                        <option value="1">Активний</option>
                                        <option value="0">Вимкнений</option>
                                    </select>
                                ) : socket.status ? (
                                    "Активний"
                                ) : (
                                    "Неактивний"
                                )}
                            </td>
                            <td className="actions compact">
                                {editingSocketId === socket.id ? (
                                    <>
                                        <button
                                            className="icon-button"
                                            onClick={() => handleSave(socket.id)}
                                            title="Зберегти"
                                        >
                                            <Save className="icon-btn" />
                                        </button>
                                        <button
                                            className="icon-button"
                                            onClick={() => setEditingSocketId(null)}
                                            title="Скасувати"
                                        >
                                            <X className="icon-btn" />
                                        </button>
                                    </>
                                ) : (
                                    <button
                                        className="icon-button"
                                        onClick={() => handleEdit(socket)}
                                        title="Редагувати"
                                    >
                                        <Edit className="icon-btn" />
                                    </button>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div >
    );

    return (
        <div>
            <div className="config-header">
                <h2>Модулі СмартДоступ</h2>
                <div className="header-buttons">
                    <button className="add-btn refresh toolbar-icon-btn" onClick={loadSockets} title="Оновити" aria-label="Оновити">
                        <RotateCw size={16} />
                    </button>
                </div>
            </div>

            {message && <p>{message}</p>}

            {isMobile ? renderMobileView() : renderDesktopView()}
        </div>
    );
};

export default SocketsOperators;
